import React from 'react';
import { TransactionItem } from '../../models/TransactionItem';
import { createCurrencyFormatter } from '../../utils/globalCurrency';
import { codeToSymbol } from '../../utils/currencyUtils';
import { useAppContext } from '../../contexts/Storage';

interface TransactionPrintableProps {
  transaction: TransactionItem;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return { date: 'N/A', time: 'N/A' };
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { date: 'Invalid Date', time: 'Invalid Time' };
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    };
  } catch {
    return { date: 'Error', time: 'Error' };
  }
};

const formatQuantity = (quantity?: number) => {
  if (quantity === undefined) return 'N/A';
  return quantity.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Standalone function for formatting transaction currency
const formatTransactionCurrency = (amount?: number, currency?: string, selectedRecordCurrency?: string) => {
  if (amount === undefined) return 'N/A';
  // Use transaction currency, fallback to selected record's currency, then default to '$'
  const currencyCode = currency || selectedRecordCurrency || '$';
  // Convert currency code to symbol if needed
  const currencySymbol = codeToSymbol(currencyCode);
  const formatter = createCurrencyFormatter(currencySymbol);
  return formatter.format(amount);
};

const TransactionPrintable = React.forwardRef<HTMLDivElement, TransactionPrintableProps>(
  ({ transaction }, ref) => {
    const { selectedRecord } = useAppContext();
    const formattedDate = formatDate(transaction.dateTime);

    const rows: { label: string; value: string }[] = [
      { label: 'Transaction ID', value: transaction.getHashedId() },
      { label: 'Date', value: formattedDate.date },
      { label: 'Time', value: formattedDate.time },
      { label: 'Action/Transaction', value: transaction.action?.transaction_entity_name || 'N/A' },
      { label: 'Item/Object', value: transaction.direct_object?.transaction_entity_name || 'N/A' },
      { label: 'Vendor/Indirect Object', value: transaction.indirect_object?.transaction_entity_name || 'N/A' },
      { label: 'Quantity', value: formatQuantity(transaction.qty) },
      { label: 'Payment Medium', value: transaction.paymentMedia?.transaction_entity_name || 'N/A' },
      { label: 'Total Amount', value: formatTransactionCurrency(transaction.amount, transaction.currency, selectedRecord?.record_currency_code) },
    ];

    const descriptions = [
      { label: 'Action', text: transaction.action?.transaction_entity_description },
      { label: 'Item', text: transaction.direct_object?.transaction_entity_description },
      { label: 'Vendor', text: transaction.indirect_object?.transaction_entity_description },
      { label: 'Payment', text: transaction.paymentMedia?.transaction_entity_description },
    ].filter((d) => !!d.text);

    return (
      <div
        ref={ref}
        style={{
          width: '480px',
          backgroundColor: '#ffffff',
          padding: '32px',
          fontFamily: "'Sora', 'Inter', system-ui, sans-serif",
          color: '#1a1a1a',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '8px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Transaction</h2>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
            ID {transaction.getHashedId()}
          </div>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: '16px 0' }} />

        {/* Detail rows */}
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            <span style={{ fontSize: '13px', color: '#666', fontWeight: 500 }}>{row.label}</span>
                        <span
              style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: row.label === 'Total Amount' ? 600 : 500,
                textAlign: 'right',
              }}
              title={row.label === 'Total Amount' ? row.value : undefined}
            >
              {row.value}
            </span>
          </div>
        ))}

        {/* Additional details */}
        {descriptions.length > 0 && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: '16px 0' }} />
            <h4 style={{ fontSize: '13px', color: '#666', fontWeight: 500, marginBottom: '12px' }}>
              Additional Details
            </h4>
            {descriptions.map((d, i) => (
              <div key={i} style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#999' }}>{d.label} Description</div>
                <div style={{ fontSize: '13px', color: '#1a1a1a' }}>{d.text}</div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }
);

TransactionPrintable.displayName = 'TransactionPrintable';

export default TransactionPrintable;
export { formatDate as formatTransactionDate, formatTransactionCurrency };
